import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, Alert, LinearProgress } from '@mui/material';
import { Add, Delete, CheckCircle, RadioButtonUnchecked, HourglassEmpty,
  Flag, CalendarToday, Euro, FolderOpen } from '@mui/icons-material';
import api from '../../services/api';

const priorityConfig = {
  niedrig: { label: 'Niedrig', color: '#9E9E9E' },
  mittel: { label: 'Mittel', color: '#1976D2' },
  hoch: { label: 'Hoch', color: '#F57C00' },
  dringend: { label: 'Dringend', color: '#D32F2F' },
};

const statusCycle = ['offen', 'in_arbeit', 'erledigt'];
const statusIcons = {
  offen: <RadioButtonUnchecked sx={{ color: '#9E9E9E' }} />,
  in_arbeit: <HourglassEmpty sx={{ color: '#F57C00' }} />,
  erledigt: <CheckCircle sx={{ color: '#2E7D32' }} />,
};

const ProjektDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [projekt, setProjekt] = useState(null);
  const [error, setError] = useState('');
  const [msOpen, setMsOpen] = useState(false);
  const [aufgabeOpen, setAufgabeOpen] = useState(false);
  const [msForm, setMsForm] = useState({ titel: '', zieldatum: '' });
  const [aufgabeForm, setAufgabeForm] = useState({
    titel: '', prioritaet: 'mittel', frist: '', meilensteinId: '', beschreibung: ''
  });

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/projekte/${id}`);
      setProjekt(res.data);
    } catch (err) { setError('Fehler beim Laden des Projekts'); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const calcProgress = (von, bis) => {
    if (!von || !bis) return 0;
    const start = new Date(von).getTime();
    const end = new Date(bis).getTime();
    const now = Date.now();
    if (now <= start) return 0;
    if (now >= end) return 100;
    return Math.round(((now - start) / (end - start)) * 100);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';
  const formatCurrency = (val) => val ? parseFloat(val).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '-';

  const handleAddMeilenstein = async () => {
    try {
      await api.post(`/projekte/${id}/meilensteine`, msForm);
      setMsOpen(false);
      setMsForm({ titel: '', zieldatum: '' });
      load();
    } catch (err) { setError('Fehler beim Erstellen des Meilensteins'); }
  };

  const handleAddAufgabe = async () => {
    try {
      const data = { ...aufgabeForm };
      if (!data.frist) delete data.frist;
      if (!data.meilensteinId) delete data.meilensteinId;
      await api.post(`/projekte/${id}/aufgaben`, data);
      setAufgabeOpen(false);
      setAufgabeForm({ titel: '', prioritaet: 'mittel', frist: '', meilensteinId: '', beschreibung: '' });
      load();
    } catch (err) { setError('Fehler beim Erstellen der Aufgabe'); }
  };

  const handleToggleStatus = async (aufgabe) => {
    const currentIdx = statusCycle.indexOf(aufgabe.status);
    const nextStatus = statusCycle[(currentIdx + 1) % statusCycle.length];
    try {
      await api.put(`/projekte/aufgaben/${aufgabe.id}`, { status: nextStatus });
      load();
    } catch (err) { setError('Fehler beim Aktualisieren'); }
  };

  const handleDeleteAufgabe = async (aufgabeId) => {
    if (window.confirm('Aufgabe wirklich löschen?')) {
      try {
        await api.delete(`/projekte/aufgaben/${aufgabeId}`);
        load();
      } catch (err) { setError('Fehler beim Löschen'); }
    }
  };

  if (!projekt) return <Box sx={{ p: 3 }}><Typography>Laden...</Typography></Box>;

  const progress = calcProgress(projekt.laufzeitVon, projekt.laufzeitBis);
  const meilensteine = projekt.meilensteine || [];
  const aufgaben = projekt.aufgaben || [];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Projekt-Info-Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h4" fontWeight="bold" color="#1B5E20">{projekt.titel}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<Euro />} onClick={() => navigate(`/finanzen/${id}`)}
                sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { borderColor: '#2E7D32', bgcolor: '#E8F5E9' } }}>
                Finanzen
              </Button>
              <Button variant="outlined" startIcon={<FolderOpen />} onClick={() => navigate(`/dokumente/${id}`)}
                sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { borderColor: '#2E7D32', bgcolor: '#E8F5E9' } }}>
                Dokumente
              </Button>
            </Box>
          </Box>
          {projekt.beschreibung && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>{projekt.beschreibung}</Typography>
          )}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Fördergeber</Typography>
              <Typography fontWeight="bold">{projekt.foerdergeber?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Budget</Typography>
              <Typography fontWeight="bold">{formatCurrency(projekt.bewilligteSumme)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Laufzeit</Typography>
              <Typography fontWeight="bold">{formatDate(projekt.laufzeitVon)} - {formatDate(projekt.laufzeitBis)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Fortschritt</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <LinearProgress variant="determinate" value={progress}
                  sx={{ flexGrow: 1, height: 10, borderRadius: 5,
                    bgcolor: '#E8F5E9', '& .MuiLinearProgress-bar': { bgcolor: '#1B5E20' } }} />
                <Typography variant="body2" fontWeight="bold">{progress}%</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Meilensteine */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="#1B5E20">Meilensteine</Typography>
        <Button variant="outlined" startIcon={<Add />} onClick={() => setMsOpen(true)}
          sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { borderColor: '#2E7D32', bgcolor: '#E8F5E9' } }}>
          Neuer Meilenstein
        </Button>
      </Box>
      {meilensteine.length === 0 ? (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center', color: '#999' }}>Noch keine Meilensteine vorhanden</Paper>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {meilensteine.map((ms) => (
            <Grid item xs={12} sm={6} md={4} key={ms.id}>
              <Card variant="outlined" sx={{ borderLeft: `4px solid ${ms.status === 'erledigt' ? '#2E7D32' : '#1B5E20'}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {ms.status === 'erledigt' ? <CheckCircle sx={{ color: '#2E7D32' }} /> : <Flag sx={{ color: '#1B5E20' }} />}
                    <Typography fontWeight="bold">{ms.titel}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarToday sx={{ fontSize: 14, color: '#999' }} />
                    <Typography variant="body2" color="text.secondary">{formatDate(ms.zieldatum)}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Aufgaben */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="#1B5E20">Aufgaben</Typography>
        <Button variant="outlined" startIcon={<Add />} onClick={() => setAufgabeOpen(true)}
          sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { borderColor: '#2E7D32', bgcolor: '#E8F5E9' } }}>
          Neue Aufgabe
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Titel</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Priorität</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Zuständig</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Frist</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aufgaben.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#999' }}>Noch keine Aufgaben vorhanden</TableCell></TableRow>
            ) : aufgaben.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell>
                  <IconButton size="small" onClick={() => handleToggleStatus(a)} title={`Status: ${a.status}`}>
                    {statusIcons[a.status] || statusIcons.offen}
                  </IconButton>
                </TableCell>
                <TableCell>{a.titel}</TableCell>
                <TableCell>
                  <Chip label={priorityConfig[a.prioritaet]?.label || a.prioritaet} size="small"
                    sx={{ bgcolor: priorityConfig[a.prioritaet]?.color || '#9E9E9E', color: 'white', fontWeight: 'bold' }} />
                </TableCell>
                <TableCell>{a.zustaendig || '-'}</TableCell>
                <TableCell>{formatDate(a.frist)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => handleDeleteAufgabe(a.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: Neuer Meilenstein */}
      <Dialog open={msOpen} onClose={() => setMsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuer Meilenstein</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Titel *" value={msForm.titel} onChange={(e) => setMsForm({...msForm, titel: e.target.value})} margin="normal" />
          <TextField fullWidth label="Zieldatum" type="date" value={msForm.zieldatum}
            onChange={(e) => setMsForm({...msForm, zieldatum: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMsOpen(false)}>Abbrechen</Button>
          <Button onClick={handleAddMeilenstein} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Neue Aufgabe */}
      <Dialog open={aufgabeOpen} onClose={() => setAufgabeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neue Aufgabe</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Titel *" value={aufgabeForm.titel} onChange={(e) => setAufgabeForm({...aufgabeForm, titel: e.target.value})} margin="normal" />
          <TextField fullWidth select label="Priorität" value={aufgabeForm.prioritaet}
            onChange={(e) => setAufgabeForm({...aufgabeForm, prioritaet: e.target.value})} margin="normal">
            {Object.entries(priorityConfig).map(([key, val]) => <MenuItem key={key} value={key}>{val.label}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Frist" type="date" value={aufgabeForm.frist}
            onChange={(e) => setAufgabeForm({...aufgabeForm, frist: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          {meilensteine.length > 0 && (
            <TextField fullWidth select label="Meilenstein-Zuordnung" value={aufgabeForm.meilensteinId}
              onChange={(e) => setAufgabeForm({...aufgabeForm, meilensteinId: e.target.value})} margin="normal">
              <MenuItem value="">Keinem Meilenstein zugeordnet</MenuItem>
              {meilensteine.map((ms) => <MenuItem key={ms.id} value={ms.id}>{ms.titel}</MenuItem>)}
            </TextField>
          )}
          <TextField fullWidth label="Beschreibung" value={aufgabeForm.beschreibung}
            onChange={(e) => setAufgabeForm({...aufgabeForm, beschreibung: e.target.value})} margin="normal" multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAufgabeOpen(false)}>Abbrechen</Button>
          <Button onClick={handleAddAufgabe} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjektDetailPage;
